import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { RoomIcon } from "./RoomIcon";
import { SegmentedProgress } from "./SegmentedProgress";

interface SpaceRowProps {
  name: string;
  subtitle: string;
  /** Average dirty % for the meter; omit to hide the meter. */
  dirtyPercent?: number;
  onClick: () => void;
  /** Extra trailing content (rendered after the meter). */
  trailing?: ReactNode;
  className?: string;
  /** Emphasize the subtitle (e.g. tasks are due). */
  highlightSubtitle?: boolean;
}

/** One room/space in a list: icon chip, name, subtitle, cleanliness meter. */
export function SpaceRow({
  name,
  subtitle,
  dirtyPercent,
  onClick,
  trailing,
  className,
  highlightSubtitle = false,
}: SpaceRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group w-full flex items-center gap-3 rounded-2xl px-2 py-2.5 -mx-2 text-left min-h-[64px]",
        "transition-colors hover:bg-surface active:bg-surface-raised",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className
      )}
    >
      <RoomIcon name={name} />
      <span className="flex-1 min-w-0">
        <span className="block font-bold text-foreground truncate">{name}</span>
        <span
          className={cn(
            "block text-[13px] truncate",
            highlightSubtitle ? "text-foreground/80 font-medium" : "text-muted-foreground"
          )}
        >
          {subtitle}
        </span>
      </span>
      {dirtyPercent !== undefined && <SegmentedProgress dirtyPercent={dirtyPercent} />}
      {trailing}
    </button>
  );
}

export default SpaceRow;
